import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import AuthModal from './AuthModal';
import LogoutModal from './LogoutModal';
import './NavBar.css';
import { ShoppingCart, CircleUser } from 'lucide-react';

const NavBar = () => {
  const loc = useLocation();
  const navigate = useNavigate();
  const [isFooterInView, setIsFooterInView] = useState(false);

  const isActive = (p) => {
    if (p === '/contact' && isFooterInView && loc.pathname === '/') {
      return 'nav-link active';
    }
    return loc.pathname === p ? 'nav-link active' : 'nav-link';
  };

  const { user, logout } = useUser();
  const [showAuth, setShowAuth] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => {
    const handleFooterInView = () => setIsFooterInView(true);
    const handleFooterOutView = () => setIsFooterInView(false);
    window.addEventListener('footer-in-view', handleFooterInView);
    window.addEventListener('footer-out-view', handleFooterOutView);
    return () => {
      window.removeEventListener('footer-in-view', handleFooterInView);
      window.removeEventListener('footer-out-view', handleFooterOutView);
    };
  }, []);

  // פונקציה ליצירת JWT בסיסי (ללא חתימה אמיתית)
  function createJwt(user) {
    if (!user) return '';
    const header = { alg: 'none', typ: 'JWT' };
    const payload = { name: user.name, email: user.email };
    function base64url(obj) {
      // קידוד ל־UTF-8
      const utf8 = encodeURIComponent(JSON.stringify(obj)).replace(/%([0-9A-F]{2})/g, function(match, p1) {
        return String.fromCharCode('0x' + p1);
      });
      return btoa(utf8).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
    }
    return base64url(header) + '.' + base64url(payload) + '.';
  }

  const handleMoodClick = (e) => {
    e.preventDefault();
    if (user && user.name && user.email) {
      const token = createJwt(user);
      console.log('JWT למד מצב רוח:', token);
      navigate('/mood', { state: { jwtToken: token } });
    } else {
      setShowAuth(true);
    }
  };

  // פונקציה לטיפול בלחיצה על הריבוע הירוק
  const handleUserBoxClick = (e) => {
    // אם המשתמש לא מחובר - פתח חלונית התחברות
    if (!user || !user.name) {
      e.preventDefault();
      setShowAuth(true);
    }
    // אם המשתמש מחובר - ה-Link יעבוד רגיל ויעביר ל-/profile
  };

  return (
    <header className="nav-bar">
      {/* right: logo */}
      <div className="nav-right">
        <Link to="/" className="logo-link">
          <img src="/לוגו שוכב.png" alt="logo" />
        </Link>
      </div>
      {/* center: navigation */}
      <nav className="nav-center">
          <Link to="/" className={isActive('/')}> 
            <span className="nav-text">דף הבית</span>
            <span className="nav-icon">♡</span>
          </Link>
         <Link to="/programs" className={isActive('/programs')}>
            <span className="nav-text">תוכניות</span>
            <span className="nav-icon">♡</span>
          </Link>
          <Link to="/minites" className={isActive('/minites')}>
            <span className="nav-text">מיניטס</span>
            <span className="nav-icon">♡</span>
          </Link>
          <Link to="/branding" className={isActive('/branding')}>
            <span className="nav-text">מיתוגים</span>
            <span className="nav-icon">♡</span>
          </Link>
          <span
            className={isActive('/mood')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={handleMoodClick}
          >
            <span className="nav-text mood-text">מד מצב רוח </span>
            <span className="nav-icon">♡</span>
          </span>
          <Link
            to={loc.pathname === '/' ? '#contact-footer' : '/#contact-footer'}
            className={isActive('/contact')}
            onClick={e => {
              e.preventDefault();
              if (loc.pathname === '/') {
                const footer = document.getElementById('contact-footer');
                if (footer) {
                  const rect = footer.getBoundingClientRect();
                  const offset = rect.bottom - window.innerHeight;
                  window.scrollBy({ top: offset, behavior: 'smooth' });
                }
              } else {
                navigate('/', { state: { scrollToContact: true } });
                setTimeout(() => {
                  const footer = document.getElementById('contact-footer');
                  if (footer) {
                    const rect = footer.getBoundingClientRect();
                    const offset = rect.bottom - window.innerHeight;
                    window.scrollBy({ top: offset, behavior: 'smooth' });
                  }
                }, 100);
              }
            }}
          >
            <span className="nav-text">צור קשר</span>
            <span className="nav-icon">♡</span>
          </Link>

      </nav>
      {/* left: user box */}
      <div className="nav-left">

        {/* כפתור התנתק */}
        {user && user.name && (
          <button
            className="logout-link-btn"
            style={{ background: 'none', border: 'none', fontWeight: 600, fontSize: '1.05rem', cursor: 'pointer', margin: '0 10px' }}
            onClick={() => setShowLogout(true)}
          >
            התנתק
          </button>
        )}
        {/* עגלת קניות */}
        <Link to="/profile/cart" className={isActive('/profile/cart')}>
          <span className="nav-icon">
            <ShoppingCart size={40} strokeWidth={1.8} />
          </span>
        </Link>
        {/* אזור אישי */}
        <Link 
          to="/profile" 
          className="nav-user-link"
          onClick={handleUserBoxClick}
        >
          <div className="user-green-box">
            <div className="user-avatar">
              {/* אייקון ב"א */}
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="4"/>
                <path d="M5.5 21c0-3 5-4.5 6.5-4.5s6.5 1.5 6.5 4.5"/>
              </svg>
            </div>
            <div className="user-name-text">
              {user && user.name ?
                (user.name.length > 8 ? user.name.slice(0, 9) + '...' : user.name)
                : 'להתחבר'}
            </div>
          </div>
        </Link>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      
      {/* פופאפ התנתקות */}
      <LogoutModal
        open={showLogout}
        onCancel={() => setShowLogout(false)}
        onConfirm={() => {
          logout();
          setShowLogout(false);
        }}
      />
    </header>
  );
};

export default NavBar;